import { useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import * as Sentry from "@sentry/react";
import {
  Controller,
  type SubmitErrorHandler,
  useForm,
  useWatch,
} from "react-hook-form";
import { useHistory } from "react-router-dom";
import Turnstile from "react-turnstile";
import { z } from "zod";

import {
  Button,
  Icon,
  Input,
  Link,
  Select,
  Text,
  toastError,
} from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { initAmplify } from "~/v2/api/auth/config";
import { CountryCombobox } from "~/v2/components/form";
import { useRedditPixel } from "~/v2/hooks/useRedditPixel";
import { Path } from "~/v2/path";
import { gTag } from "~/v2/utils/gTag";
import { UserActions } from "../../api/auth";
import {
  useGetCountries,
  useGetIpAddress,
  useMutationRequest,
} from "../../api/hooks";
import { noLastNameRequired } from "../dashboard/onboarding/constants";
import { CONTACT_URL, DOCS_URL, type SignUpLocationState } from ".";

const MIN_PASSWORD_LENGTH = 12;
const MIN_LAST_NAME_LENGTH = 2;

const referralSources = {
  reddit: "Reddit",
  chatgpt: "ChatGPT",
  google: "Google Search",
  referral: "Friend/Referral",
  youtube: "YouTube",
  bing: "Bing",
  tradingview: "TradingView",
  other: "Other",
};

const getIsLastNameRequired = (alpha3Country: string) =>
  !noLastNameRequired.includes(alpha3Country);

const zCreate = z.object({
  email: z.string().email({ message: "You must provide an email" }),
  password: z
    .string()
    .min(
      MIN_PASSWORD_LENGTH,
      `Password must be at least ${MIN_PASSWORD_LENGTH} characters`,
    ),
  last_name: z
    .string()
    .min(
      MIN_LAST_NAME_LENGTH,
      `Must be at least ${MIN_LAST_NAME_LENGTH} characters`,
    )
    .optional(),
  first_name: z.string(),
  country: z.string(),
  referral: z.object({
    // Casting because Zod's typing requires at least one item in the array,
    // and Object.keys doesn't guarantee that
    source: z
      .enum(Object.keys(referralSources) as [string, ...string[]])
      .optional(),
    other: z.string().optional(),
  }),
});

type Fields = z.infer<typeof zCreate>;

type SignupErrorCategory =
  | "captcha"
  | "email_domain"
  | "lambda_other"
  | "username_exists"
  | "waf_blocked"
  | "unknown";

// Classify pre-signup Lambda errors (alpacahq/aws-cognito-pre-signup-hook)
// wrapped by Cognito as UserLambdaValidationException, so a captcha failure
// isn't misreported as an invalid email domain.
const classifySignupError = (error: Error): SignupErrorCategory => {
  if (error.name === "UsernameExistsException") {
    return "username_exists";
  }

  if (error.name === "ForbiddenException") {
    return "waf_blocked";
  }

  if (error.name !== "UserLambdaValidationException") {
    return "unknown";
  }

  const message = error.message?.toLowerCase() ?? "";

  if (
    message.includes("client validation failed") ||
    message.includes("validation response is empty") ||
    message.includes("sign-up ip address is empty")
  ) {
    return "captcha";
  }

  if (message.includes("email address domain may not be used")) {
    return "email_domain";
  }

  return "lambda_other";
};

const getSignupErrorMessage = (
  error: Error,
  category: SignupErrorCategory,
): string => {
  switch (category) {
    case "captcha":
      return "We couldn't complete the captcha verification. Please refresh the page and try again. If the problem persists, contact support@alpaca.markets.";
    case "email_domain":
      return "Sorry, we couldn't complete your sign-up request because the email domain you provided is not allowed. Please try a different email address or contact support@alpaca.markets for assistance.";
    case "lambda_other":
      return "We couldn't complete your sign-up request. Please try again, or contact support@alpaca.markets if the problem persists.";
    case "username_exists":
      return "An account with this email may already exist. Check your inbox for a verification link, or try signing in instead.";
    case "waf_blocked":
      return "We're unable to process your sign-up right now. Please try again later or contact support@alpaca.markets if the problem persists.";
    case "unknown":
      return error.message;
  }
};

const SILENT_ERRORS: SignupErrorCategory[] = ["username_exists"];
const WARNING_ERRORS: SignupErrorCategory[] = ["waf_blocked", "captcha"];

export const Create = () => {
  const history = useHistory<SignUpLocationState>();

  const { redditTrackSignUp } = useRedditPixel();

  const { countries, nameToAlpha3 } = useGetCountries();

  const { ipAddress } = useGetIpAddress();

  const form = useForm<Fields>({
    mode: "onBlur",
    resolver: zodResolver(
      zCreate.superRefine(({ country, last_name }, refinementContext) => {
        const alpha3Country = nameToAlpha3(country);
        const isCountryValid = alpha3Country !== undefined;
        const isLastNameRequired = getIsLastNameRequired(alpha3Country ?? "");

        if (!isCountryValid) {
          refinementContext.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Select country from dropdown",
            path: ["country"],
          });
        }

        if (
          isLastNameRequired &&
          (last_name?.length ?? 0) < MIN_LAST_NAME_LENGTH
        ) {
          refinementContext.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Last name must be at least ${MIN_LAST_NAME_LENGTH} characters`,
            path: ["last_name"],
          });
        }
      }),
    ),
  });

  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string>("");

  const togglePasswordVisibility = () =>
    setIsPasswordVisible((prevState) => !prevState);

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = form;

  // real-time country value in the form (watched)
  const country = useWatch({ control, name: "country" });

  const { mutate } = useMutationRequest(
    async (fields: Fields) => {
      initAmplify();
      return await UserActions.registerUser({
        firstName: fields.first_name,
        lastName: fields.last_name ?? "",
        country: nameToAlpha3(fields.country) || "",
        email: fields.email.toLowerCase(),
        password: fields.password,
        countryOfCitizenship: fields.country,
        validationResponse: turnstileToken,
        signupIPAddress: ipAddress,
        referralSource:
          fields.referral.source === "other"
            ? (fields.referral.other ?? "other")
            : (fields.referral.source ?? ""),
      });
    },
    {
      onSuccess: (data, { country, email, last_name, first_name }) => {
        // maybe they fucked up their first account
        window.localStorage?.removeItem("account_type_step");
        window.localStorage?.removeItem("patch_funding_after_kyc");
        window.localStorage?.removeItem("patch_employment_after_kyc");

        // track in amplitude
        AmplitudeProvider.dispatch(
          "onboardingv2_signup_completed",
          JSON.stringify({ country_of_tax_residence: country }),
        );

        // track in Google
        gTag({
          eventName: "conversion",
          eventParams: {
            send_to: "AW-835481437/wBuDCI_n55ABEN3esY4D",
          },
        });

        // track in RedditPixel
        redditTrackSignUp({
          email,
        });

        // push to verification page
        history.push("/signup/pending", {
          email: email,
          last_name: last_name ?? "",
          first_name: first_name,
          user_sub: data?.userSub ?? "",
        });
      },
      onError: (error, variables) => {
        const category = classifySignupError(error);
        const email = variables.email.toLowerCase();
        const emailDomain = email.includes("@")
          ? email.split("@")[1]
          : undefined;

        if (SILENT_ERRORS.includes(category)) {
          return;
        }

        Sentry.captureException(error, {
          level: WARNING_ERRORS.includes(category) ? "warning" : "error",
          tags: {
            component: "signup_create_account",
            category,
            cognito_error: error.name,
          },
          user: {
            email,
            ip_address: ipAddress,
          },
          extra: {
            email,
            email_domain: emailDomain,
            signup_ip_address: ipAddress,
            country: variables.country,
            country_alpha3: nameToAlpha3(variables.country),
            referral_source: variables.referral.source,
            has_last_name: Boolean(variables.last_name),
            has_turnstile_token: Boolean(turnstileToken),
            turnstile_token_prefix: turnstileToken
              ? `${turnstileToken.slice(0, 8)}...`
              : undefined,
            cognito_error_message: error.message,
          },
        });

        toastError(`Sign up Error: ${getSignupErrorMessage(error, category)}`);
      },
    },
  );

  // some countries don't require last name
  const isLastNameRequired = getIsLastNameRequired(
    nameToAlpha3(country ?? "") ?? "",
  );

  const onSubmit = async (data: Fields) => {
    // Edge case - user bypassed disabled prop on submit button to reach this
    if (!turnstileToken) {
      toastError("Error: Please complete captcha");
      return;
    }

    mutate(data);
  };

  const onSubmitInvalid: SubmitErrorHandler<Fields> = () => {
    toastError("Invalid form");
  };

  return (
    <div className="flex flex-col justify-center gap-5 px-8">
      <form
        style={{ width: "100%" }}
        onSubmit={handleSubmit(onSubmit, onSubmitInvalid)}
      >
        <div className="mb-7 text-center">
          <h1 className="font-bold text-2xl">
            Create Your Trading API Account
          </h1>
        </div>
        <div className="flex flex-col items-start gap-1">
          <div className="flex w-full flex-col items-start gap-1 md:flex-row md:gap-3">
            <div className="w-full">
              <Controller
                name="first_name"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    id="first_name"
                    label="First Name"
                    status={error ? "error" : "neutral"}
                    errorMsg={error?.message}
                    placeholder="John"
                    msgPadded
                    data-testid="first-name-input"
                    {...field}
                  />
                )}
              />
            </div>
            <div className="w-full">
              <Controller
                name="last_name"
                control={control}
                rules={{ required: isLastNameRequired }}
                render={({ field, fieldState: { error } }) => (
                  <Input
                    id="last_name"
                    label="Last Name"
                    status={error ? "error" : "neutral"}
                    errorMsg={error?.message}
                    placeholder="Doe"
                    msgPadded
                    data-testid="last-name-input"
                    {...field}
                  />
                )}
              />
            </div>
          </div>

          <div className="w-full">
            <Controller
              name="country"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field, fieldState: { error } }) => (
                <CountryCombobox
                  name={field.name}
                  label="Country of Tax Residence"
                  placeholder="United States of America"
                  status={error ? "error" : "neutral"}
                  errorMsg={error?.message}
                  items={countries}
                  value={field.value}
                  onChange={field.onChange}
                  msgPadded
                />
              )}
            />
          </div>
          <div className="w-full">
            <Controller
              name="email"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  id="email"
                  label="Email"
                  type="email"
                  placeholder="john.doe@example.com"
                  status={error ? "error" : "neutral"}
                  errorMsg={error?.message}
                  msgPadded
                  data-testid="email-input"
                  {...field}
                />
              )}
            />
          </div>
          <div className="w-full">
            <Controller
              name="password"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState: { error } }) => (
                <Input
                  id="password"
                  label="Password"
                  type={isPasswordVisible ? "text" : "password"}
                  placeholder="••••••••••••"
                  status={error ? "error" : "neutral"}
                  errorMsg={error?.message}
                  rightIcon={
                    <Icon
                      className="cursor-pointer stroke-2"
                      name={isPasswordVisible ? "Eye" : "EyeSlash"}
                      onClick={togglePasswordVisibility}
                    />
                  }
                  msgPadded
                  data-testid="password-input"
                  {...field}
                />
              )}
            />
          </div>
          <div className="flex w-full flex-col">
            <div className="w-full">
              <Controller
                name="referral.source"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    label="How did you hear about us?"
                    options={Object.entries(referralSources).map(
                      ([value, label]) => ({ value, label }),
                    )}
                    msgPadded
                  />
                )}
              />
            </div>
            <div className="w-full">
              {form.watch("referral.source") === "other" && (
                <Controller
                  name="referral.other"
                  control={control}
                  render={({ field }) => <Input {...field} msgPadded />}
                />
              )}
            </div>
          </div>

          <Turnstile
            sitekey={window.env.TURNSTILE_SITE_KEY}
            theme="light"
            onVerify={(token) => setTurnstileToken(token)}
          />

          <Button
            variant="v2/filled"
            type="submit"
            className="mt-4 w-full"
            disabled={!turnstileToken || isSubmitting}
            data-testid="submit-button"
          >
            Sign up
          </Button>
        </div>
      </form>

      <div className="flex flex-col justify-center gap-4">
        <Text className="text-center text-base">
          <span className="text-gray-500">Already have an account?</span>
          &nbsp;
          <Link
            onClick={() => history.push(Path.ROUTE_LOGIN)}
            className="text-gray-900 underline"
            data-testid="return-to-login"
          >
            Sign in
          </Link>
        </Text>

        <div className="flex flex-col justify-center gap-1">
          <Text className="text-center text-gray-500 text-sm">
            Having trouble?{" "}
            <Link href={CONTACT_URL} rel="noopener">
              Contact support
            </Link>
          </Text>

          <Text className="text-center text-gray-500 text-sm">
            <Link href={DOCS_URL} rel="noopener">
              Browse our Docs
            </Link>{" "}
            for use cases, sample code, and how to get started.
          </Text>
        </div>
      </div>
    </div>
  );
};
